import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./PortfolioGalleryPage.module.scss";

import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type ImgMeta = { name: string; size: number; preview: string };

export default function PortfolioGalleryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [gallery, setGallery] = useState<ImgMeta[]>([]);
  const [successModal, setSuccessModal] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  const token = axios.defaults.headers.common.Authorization;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) {
          setUser(data[1]);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  const openPicker = () => fileInputRef.current?.click();

  const showSuccessModal = (message: string) => {
    setSuccessModal(message);
    setTimeout(() => setSuccessModal(null), 2000);
  };

  const uploadFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("is_confidential", "false");
      formData.append("user_file_name", file.name);
      formData.append("keywords", "");
      formData.append("category_ids", "9");

      await axios.post(`/consent_clause/upload_user_media_to_s3`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });

      showSuccessModal(`${file.name} uploaded successfully ✅`);
    } catch (err) {
      console.error("Upload failed:", err);
      showSuccessModal(`❌ Failed to upload ${file.name}`);
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const accepted: ImgMeta[] = [];

    Array.from(files).forEach((f) => {
      const okType = /image\/(png|jpeg)/i.test(f.type);
      const okSize = f.size <= 5 * 1024 * 1024;

      if (okType && okSize) {
        const preview = URL.createObjectURL(f);
        accepted.push({ name: f.name, size: f.size, preview });
        uploadFile(f);
      } else {
        showSuccessModal(`${f.name} is invalid ❌`);
      }
    });

    if (accepted.length === 0) return;

    setGallery((prev) => [...prev, ...accepted]);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={4}
          rightLabel="50% Complete"
          onStepClick={(step) => navigate(step.path)}
        />

        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.title}>Portfolio gallery</h1>
            <p className={styles.subtitle}>
              Upload before-and-after photos, treatment videos, or educational
              clips. These visuals can be included in your consent forms and used as
              a marketing pack to highlight your work.
            </p>
          </div>

          <div className={styles.progressCard}>
            <span className={styles.progressValue}>{gallery.length}</span>
            <span className={styles.progressLabel}>Images added</span>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h2 className={styles.cardTitle}>Upload images</h2>
              <p className={styles.cardSubtitle}>
                Add high-quality portfolio visuals to showcase your work.
              </p>
            </div>
          </div>

          <div
            className={styles.drop}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload portfolio images"
          >
            <div className={styles.dropInner}>
              <div className={styles.uIcon} aria-hidden="true">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path d="M12 16V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path
                    d="M7 9l5-5 5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div className={styles.dropTitle}>Drag and drop images here</div>
              <div className={styles.dropHint}>PNG or JPG, max 5MB each</div>

              <button
                type="button"
                className={styles.chooseBtn}
                onClick={openPicker}
              >
                Add images
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                className={styles.fileInput}
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          </div>
        </section>

        <section className={`${styles.card} ${styles.tipsCard}`}>
          <h3 className={styles.tipsTitle}>Portfolio tips for the best results:</h3>
          <ul className={styles.tipsList}>
            <li>
              Use it as both a practice portfolio and a source of content for your
              website or promotional materials.
            </li>
            <li>
              Showcase your individual expertise as a dentist, not just your
              practice’s brand.
            </li>
            <li>
              Include before-and-after examples of different procedures to build
              patient trust.
            </li>
            <li>
              Share a variety of treatments to demonstrate your full skill set.
            </li>
            <li>
              Always use high-quality images and videos to put your best work forward.
            </li>
          </ul>
        </section>

        <section className={styles.gallerySection}>
          <div className={styles.gallerySummaryCard}>
            <div className={styles.gallerySummaryTop}>
              <div>
                <h3 className={styles.galleryTitle}>Portfolio preview</h3>
                <p className={styles.gallerySubtitle}>
                  {gallery.length === 0
                    ? "No images uploaded yet."
                    : `${gallery.length} image${gallery.length > 1 ? "s" : ""} ready for preview.`}
                </p>
              </div>

              <div className={styles.galleryBadge}>
                {gallery.length} item{gallery.length !== 1 ? "s" : ""}
              </div>
            </div>

            {gallery.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🖼️</div>
                <div className={styles.emptyTitle}>No portfolio images yet</div>
                <div className={styles.emptyText}>
                  Upload a few images to build your gallery preview here.
                </div>
              </div>
            ) : (
              <ul className={styles.previewGrid}>
                {gallery.map((m, i) => (
                  <li key={i} className={styles.previewCard}>
                    <div className={styles.previewThumbWrap}>
                      <img
                        src={m.preview}
                        alt={m.name}
                        className={styles.previewThumb}
                      />
                    </div>

                    <div className={styles.previewMeta}>
                      <span className={styles.previewName} title={m.name}>
                        {m.name}
                      </span>
                      <span className={styles.previewSize}>
                        {(m.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <div className={styles.actions}>
          <button
            className={styles.btnSecondary}
            onClick={() => navigate("/socialLinks")}
          >
            Back
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => navigate("/consentTemplates")}
          >
            Next
          </button>
        </div>
      </main>

      {successModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox}>{successModal}</div>
        </div>
      )}

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>
    </div>
  );
}